import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

/** Shared Tailwind class string for the sticky search header wrapper in pickers. */
export const PICKER_HEADER_CLASS = "px-3 py-2 sticky top-0 bg-popover z-10 border-b border-border";

/** Shared Tailwind class string for picker text inputs. */
export const PICKER_INPUT_CLASS =
  "h-8 text-sm bg-secondary border-border focus-visible:ring-0 focus-visible:ring-offset-0";

/** Shared Tailwind classes for pill-style option buttons. */
export function pickerPillClass(selected: boolean): string {
  return selected
    ? "px-2.5 py-1 text-sm rounded-full border bg-secondary border-secondary text-secondary-foreground"
    : "px-2.5 py-1 text-sm rounded-full border bg-transparent border-border text-foreground hover:bg-secondary";
}

/**
 * Shared keyboard navigation hook for picker components.
 *
 * Handles arrow-key navigation with clamping and Enter/Tab to submit.
 * Each picker can specify which arrow-key orientation it uses.
 */
export function usePickerKeyboard(opts: {
  itemCount: number;
  onSelect: (index: number) => void;
  defaultIndex?: number;
  orientation?: "vertical" | "horizontal" | "both";
}): {
  selectedIndex: number;
  setSelectedIndex: (i: number) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
} {
  const { itemCount, onSelect, defaultIndex = 0, orientation = "vertical" } = opts;

  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);

  // Clamp selectedIndex when item count shrinks (e.g. filtering reduces options)
  useEffect(() => {
    setSelectedIndex((prev) => (prev >= itemCount ? 0 : prev));
  }, [itemCount]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (itemCount === 0) return;

      const vNext = e.key === "ArrowDown";
      const vPrev = e.key === "ArrowUp";
      const hNext = e.key === "ArrowRight";
      const hPrev = e.key === "ArrowLeft";

      const isNext =
        orientation === "vertical" ? vNext : orientation === "horizontal" ? hNext : vNext || hNext;
      const isPrev =
        orientation === "vertical" ? vPrev : orientation === "horizontal" ? hPrev : vPrev || hPrev;

      if (isNext) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => Math.min(prev + 1, itemCount - 1));
        return;
      }
      if (isPrev) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        return;
      }

      if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
        e.preventDefault();
        e.stopPropagation();
        onSelect(selectedIndex);
      }
    },
    [itemCount, orientation, selectedIndex, onSelect],
  );

  return { selectedIndex, setSelectedIndex, handleKeyDown };
}

/** Auto-focus a ref on mount. Useful for portal-rendered picker components. */
export function useAutoFocusRef<T extends HTMLElement>(): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);
  return ref;
}

/**
 * Handle Enter/Tab submission in pickers with a filterable options list.
 *
 * Prefers the currently selected filtered option, falls back to the first
 * option, then to the raw free-text input. Returns `true` if the event was
 * consumed (so the caller knows whether to stopPropagation).
 */
export function handlePickerSubmit(
  e: React.KeyboardEvent,
  opts: {
    filteredOptions: string[];
    selectedIndex: number;
    freeText: string;
    onSelect: (value: string) => void;
  },
): boolean {
  if (e.key !== "Enter" && !(e.key === "Tab" && !e.shiftKey)) return false;

  const selected = opts.filteredOptions[opts.selectedIndex] ?? opts.filteredOptions[0];
  const text = opts.freeText.trim();

  if (selected) {
    e.preventDefault();
    e.stopPropagation();
    opts.onSelect(selected);
    return true;
  }
  if (text) {
    e.preventDefault();
    e.stopPropagation();
    opts.onSelect(text);
    return true;
  }
  return false;
}

/**
 * Scroll a `[data-index]` element into view within its scrollable ancestor.
 * Shared across pickers that have scrollable option lists.
 */
export function scrollToItem(container: HTMLElement | null, index: number): void {
  if (!container) return;
  const el = container.querySelector(`[data-index="${index}"]`) as HTMLElement | null;
  if (!el) return;

  const elTop = el.offsetTop;
  const elBottom = elTop + el.offsetHeight;
  const viewTop = container.scrollTop;
  const viewBottom = viewTop + container.clientHeight;

  if (elBottom > viewBottom) {
    container.scrollTop = elBottom - container.clientHeight;
  } else if (elTop < viewTop) {
    container.scrollTop = elTop;
  }
}

/**
 * Walk up the DOM from a ref to find the nearest scrollable ancestor.
 * Shared across pickers that need programmatic scroll control.
 */
export function useFindScrollParent(
  ref: React.RefObject<HTMLElement | null>,
): React.RefObject<HTMLElement | null> {
  const scrollRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    let parent = ref.current.parentElement;
    while (parent) {
      const { overflowY } = getComputedStyle(parent);
      if (overflowY === "auto" || overflowY === "scroll") {
        scrollRef.current = parent;
        return;
      }
      parent = parent.parentElement;
    }
    // ref is a stable RefObject — this runs once on mount.
    // Do NOT add ref.current; mutating .current is not observable by React.
  }, [ref]);
  return scrollRef;
}
